/* eslint-disable max-len */
/* eslint-disable no-console, react/prop-types */
import {InfoCircleOutlined, LockFilled} from '@ant-design/icons';
import {Flex, Tooltip} from 'antd';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import industryBenchmarkBg from '../../../../../assets/skeleton/industryBenchmark.png';
import PilComp from '../../../../../components/DisplayComponent/PilComp';
import UpgradeButton from '../../../../../components/UpgradeButton/UpgradeButton';
import {Routes} from '../../../../../constants/Routes';

import {formatBenchmarkValue} from './utils';

const PILL_ABOVE_COLOR = '#DA1E28';
const PILL_BELOW_COLOR = '#435B37';
const PILL_EQUAL_COLOR = '#024064';

interface BenchmarkCardItem {
  id: string;
  label: string;
  value: number;
  benchmark: number;
  unit: string;
  tooltip?: string;
  category?: string;
}

// Share of the bar track a value fills, scaled against the larger of the
// company and benchmark values.
const barWidth = (value: number, max: number): string =>
  `${max > 0 ? Math.max(0, (value / max) * 100) : 0}%`;

const BenchmarkCard: React.FC<{
  item: BenchmarkCardItem;
  isBlocked?: boolean;
  showSummaryLine?: boolean;
}> = ({item, isBlocked = false, showSummaryLine = false}) => {
  const navigate = useNavigate();
  const isAbove = item.value > item.benchmark;
  const isBelow = item.value < item.benchmark;

  const pillColor = isAbove
    ? PILL_ABOVE_COLOR
    : isBelow
      ? PILL_BELOW_COLOR
      : PILL_EQUAL_COLOR;

  const cardStatusClass = isAbove
    ? 'industryBenchmarkCard--above'
    : isBelow
      ? 'industryBenchmarkCard--below'
      : 'industryBenchmarkCard--equal';

  const formattedValue = formatBenchmarkValue(
    item.value,
    item.unit,
    item.benchmark
  );
  const formattedBenchmark = formatBenchmarkValue(
    item.benchmark,
    item.unit,
    item.value
  );

  let summaryText = '';
  let summaryColor = '';
  let deltaText = '';
  if (item.benchmark > 0) {
    const diffPercent = Math.round((Math.abs(item.value - item.benchmark) / item.benchmark) * 100);
    if (isAbove) {
      summaryText = `${item.label} are ${diffPercent}% more than the Industry Benchmark.`;
      summaryColor = '#EB606A'; // Red
      deltaText = `▲ ${diffPercent}% above benchmark`;
    } else if (isBelow) {
      summaryText = `${item.label} are ${diffPercent}% less than the Industry Benchmark.`;
      summaryColor = '#4CAF50'; // Green
      deltaText = `▼ ${diffPercent}% below benchmark`;
    } else {
      summaryText = `${item.label} are equal to the Industry Benchmark.`;
      summaryColor = '#374151';
      deltaText = 'Equal to benchmark';
    }
  } else if (item.value > 0) {
    summaryText = `${item.label} are 100% more than the Industry Benchmark.`;
    summaryColor = '#EB606A';
    deltaText = '▲ 100% above benchmark';
  } else {
    summaryText = `${item.label} are equal to the Industry Benchmark.`;
    summaryColor = '#374151';
    deltaText = 'Equal to benchmark';
  }

  const maxValue = Math.max(item.value, item.benchmark);

  const comparisonRows = [
    {
      key: 'company',
      label: 'Company',
      formatted: formattedValue,
      width: barWidth(item.value, maxValue)
    },
    {
      key: 'benchmark',
      label: 'Benchmark',
      formatted: formattedBenchmark,
      width: barWidth(item.benchmark, maxValue)
    }
  ];

  if (isBlocked) {
    return (
      <div
        className="industryBenchmarkCard industryBenchmarkCard--locked"
        style={{
          backgroundImage: `url(${industryBenchmarkBg})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        }}
      >
        <Flex
          className="industryBenchmarkCardHeader"
          justify="space-between"
          align="center"
          gap={8}
        >
          <PilComp
            value={item.label}
            color="#F5DA84"
            style={{
              padding: '6px 12px',
              color: '#101223',
              font: 'normal normal 700 12px Montserrat'
            }}
          />
          <Tooltip title={item.tooltip}>
            <InfoCircleOutlined className="industryBenchmarkCardInfo" />
          </Tooltip>
        </Flex>
        <Flex
          className="industryBenchmarkLockedWrap"
          vertical
          align="center"
          gap={18}
        >
          <div className="industryBenchmarkLockIconWrap">
            <LockFilled className="industryBenchmarkLockIcon" />
          </div>
          <Flex vertical align="center" gap={2}>
            <span className="industryBenchmarkLockedTitle">
              Unlock Advanced
            </span>
            <span className="industryBenchmarkLockedSubtitle">
              Scope 3 insights
            </span>
          </Flex>
          <div
            onClick={() => {
              navigate(Routes.PRICING_DETAILS);
            }}
          >
            <UpgradeButton
              title="Upgrade"
              style={{
                height: 29,
                minWidth: 102,
                borderRadius: 4,
                font: 'normal normal 700 14px Montserrat',
                color: '#1B1D2A'
              }}
            />
          </div>
        </Flex>
      </div>
    );
  }

  return (
    <div className={`industryBenchmarkCard ${cardStatusClass}`}>
      <Flex vertical gap={10}>
        <Flex
          className="industryBenchmarkCardHeader"
          justify="space-between"
          align="center"
          gap={21}
        >
          <PilComp
            value={item.label}
            color={pillColor}
            style={{
              padding: '5px 10px',
              color: '#fff',
              font: 'normal normal 600 14px Montserrat'
            }}
          />
          <Tooltip title={item.tooltip}>
            <InfoCircleOutlined className="industryBenchmarkCardInfo" />
          </Tooltip>
        </Flex>
        <span className="industryBenchmarkDelta">{deltaText}</span>
      </Flex>
      <Flex className="industryBenchmarkBars" vertical gap={22}>
        {comparisonRows.map((row) => (
          <Flex key={row.key} vertical gap={10}>
            <Flex className="industryBenchmarkBarLabel" justify="space-between">
              <span>{row.label}</span>
              <span>{`${row.formatted.formatted} ${row.formatted.unit}`}</span>
            </Flex>
            <div
              className={`industryBenchmarkBarTrack industryBenchmarkBarTrack--${row.key}`}
            >
              <div
                className="industryBenchmarkBarFill"
                style={{width: row.width}}
              />
            </div>
          </Flex>
        ))}
      </Flex>
      <Flex gap={8}>
        {comparisonRows.map((row) => (
          <Flex
            key={row.key}
            className="industryBenchmarkTotalBox"
            vertical
            justify="center"
            gap={10}
          >
            <Flex align="center" gap={6}>
              <span
                className={`industryBenchmarkLegendDot industryBenchmarkLegendDot--${row.key}`}
              />
              <span className="industryBenchmarkLegendText">{row.label}</span>
            </Flex>
            <span>
              <span className="industryBenchmarkValueBold">
                {row.formatted.formatted}
              </span>
              <span className="industryBenchmarkValueSmall">
                {` ${row.formatted.unit}`}
              </span>
            </span>
          </Flex>
        ))}
      </Flex>
      {showSummaryLine && summaryText && (
        <div style={{padding: '0 16px 16px', fontSize: '11px', textAlign: 'center'}}>
          <span style={{color: summaryColor, fontWeight: 600}}>{summaryText}</span>
        </div>
      )}
    </div>
  );
};

export default BenchmarkCard;
